import { MapPin, Navigation } from "lucide-react";

const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY;
const TILE_Z = 15;
const TILE_PX = 512;

// Web-Mercator tile math: returns the tile x/y and the marker's fractional
// position (0..1) within that tile, so we can overlay a pin at the exact spot.
function tileInfo(lat, lng, z) {
  const n = 2 ** z;
  const xf = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const yf = ((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n;
  const xt = Math.floor(xf);
  const yt = Math.floor(yf);
  return { xt, yt, fx: xf - xt, fy: yf - yt };
}

/**
 * Compact static map thumbnail for an admin job location.
 * Uses a single MapTiler hybrid tile (no Static Maps API needed) with a
 * pin overlay, linking to Google Maps directions. Falls back to a link.
 */
export default function LocationThumb({ lat, lng, size = 120 }) {
  if (lat == null || lng == null) return null;

  const directions = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

  if (!MAPTILER_KEY) {
    return (
      <a
        href={directions}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
        data-testid="location-directions-link"
      >
        <Navigation className="w-3.5 h-3.5" />
        Get directions
      </a>
    );
  }

  const { xt, yt, fx, fy } = tileInfo(lat, lng, TILE_Z);
  const tileUrl = `https://api.maptiler.com/maps/hybrid/${TILE_Z}/${xt}/${yt}.jpg?key=${MAPTILER_KEY}`;

  return (
    <a
      href={directions}
      target="_blank"
      rel="noopener noreferrer"
      className="block relative rounded-lg overflow-hidden border border-slate-200 hover:opacity-95 transition-opacity"
      style={{ width: size, height: size }}
      title="Open directions in Google Maps"
      data-testid="location-thumb"
    >
      <img
        src={tileUrl}
        alt="Job location"
        style={{ width: size, height: size, display: "block" }}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <span
        style={{
          position: "absolute",
          left: fx * size,
          top: fy * size,
          transform: "translate(-50%, -100%)",
        }}
      >
        <MapPin className="w-6 h-6 text-orange-500 fill-orange-500 drop-shadow" />
      </span>
      <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] py-0.5 flex items-center justify-center gap-1">
        <Navigation className="w-3 h-3" />
        Get directions
      </span>
    </a>
  );
}
